import React from 'react';
import { Box, Typography, CardContent } from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  ResponsiveContainer,
  Cell,
  ReferenceLine,
  Tooltip,
  CartesianGrid,
} from 'recharts';
import { ChartDataPoint } from './DashboardDeepDiveDropReasons';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';


/** What Recharts hands a custom tooltip: the hovered point, when there is one. */
interface ChartTooltipProps {
  active?: boolean;
  payload?: { payload: ChartDataPoint }[];
}

const CustomTooltip = ({ active, payload }: ChartTooltipProps) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;

    const isPositive = data.value > 0;
    return (
      <Box
        sx={{
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          padding: '12px',
          border: '1px solid #ccc',
          borderRadius: '8px',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
        }}
      >
        <Typography variant="body2" fontWeight="bold" textAlign="center">
          {data.fullName}
        </Typography>
        {isPositive && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#F44336',
            }}
          >
            <TrendingDownIcon />
            <Typography variant="body2" color="#F44336" fontWeight="bold">
              +{data.value}% Rispetto al benchmark
            </Typography>
          </Box>
        )}
        {!isPositive && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#4CAF50',
            }}
          >
            <TrendingUpIcon />
            <Typography variant="body2" color="#4CAF50" fontWeight="bold">
              {data.value}% Rispetto al benchmark
            </Typography>
          </Box>
        )}
      </Box>
    );
  }
  return null;
};

interface DropReasonChartProps {
  title: string;
  data: ChartDataPoint[];
  benchmarkValue: number;
}

const DashboardDropReasonPerMemberChart: React.FC<DropReasonChartProps> = ({
  title,
  data,
  benchmarkValue,
}) => {
  return (
    <Box
      sx={{
        width: '100%',
        mb: 2,
        borderRadius: '8px',
        border: '1px solid rgba(224, 224, 224, 1)',
        backgroundColor: 'white',
      }}
    >
      <CardContent>
        <Typography
          variant="h6"
          gutterBottom
          align="center"
          sx={{ fontSize: '0.9rem', fontWeight: 'bold' }}
        >
          {title}
        </Typography>
        <ResponsiveContainer width="100%" minWidth={500} height={500}>
          <BarChart
            data={data}
            margin={{ top: 20, right: 50, left: 20, bottom: 50 }}
          >
            <CartesianGrid stroke="#ccc" strokeDasharray="3 3" />
            <XAxis
              dataKey="name"
              angle={60}
              textAnchor="start"
              fontSize={14}
              interval={0}
            />
            <YAxis
              reversed
              tickFormatter={(value: number) =>
                `${value > 0 ? '+' : ''}${value}%`
              }
              strokeDasharray="3 3"
            />
            <ReferenceLine
              y={0}
              stroke="gray"
              strokeWidth={2}
              strokeDasharray="none"
            />
            <Tooltip content={<CustomTooltip />} />
            <Bar dataKey="value" fill="#8884d8">
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
        <Typography
          variant="body2"
          color="text.secondary"
          align="center"
          sx={{ mt: 1 }}
        >
          Differenza rispetto al benchmark ({benchmarkValue.toFixed(1)}%)
        </Typography>
      </CardContent>
    </Box>
  );
};

export default DashboardDropReasonPerMemberChart;
